import React, { useEffect, useRef, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnIcon,
  History as HistoryIcon,
  Search as SearchIcon,
  InfoOutlined,
  CheckCircle as ApproveIcon,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import ChangeHistoryDialog from "./ChangeHistoryDialog.js";
import ColumnSelectorDialog from "./ColumnSelectorDialog.js";
import OpexService from "../service/opexService.js";
import LevelOpexService from "../service/levelOpexService.js";
import { UserContext } from "../context/UserContext.js";
import OpexSearchBox from "./OpexSearchBox.js";
import { useNavigate } from "react-router-dom";
import {
  formatDateInItalian,
} from "../utils/general.js";


const OpexTable = ({
  onlyAllocation, 
  title, 
  href,
  pendingHref,
  historyHref
}) => {
  const [cdcList, setCdcList] = useState([]);
  const [accountsList, setAccountsList] = useState([]);
  const expenseItemsConfigurations = useRef([]);
  // Formatter combinato per gestire sia i valori approvati che quelli pending
  const combinedFormatter = function (cell) {
    const rowData = cell.getRow().getData();
    const field = cell.getField();
    const value = cell.getValue();
    // CASO 1: Se il record è approvato, mostra il valore approvato
    if (rowData.recentlyApproved) {
      // Per utenti admin o validator, aggiungi un indicatore speciale se il campo è stato modificato dopo l'approvazione
      if (
        (bestRole === "Admin" || bestRole === "Validator") &&
        rowData.hasPendingChanges &&
        rowData.pendingValues &&
        rowData.pendingValues[field] !== undefined
      ) {
        const pendingValue = rowData.pendingValues[field];

        // Se il valore pending è diverso dal valore attuale
        if (String(pendingValue) !== String(value)) {
          // Crea un elemento div per contenere il valore con indicatore speciale
          const container = document.createElement("div");
          container.style.position = "relative";
          container.style.width = "100%";
          container.style.display = "flex";
          container.style.alignItems = "center";

          // Valore con indicatore di modifica
          const valueSpan = document.createElement("span");
          valueSpan.textContent =
            value !== null && value !== undefined ? value : "";
          valueSpan.style.color = "#2E7D32"; // Verde scuro
          valueSpan.style.fontWeight = "bold";
          valueSpan.style.position = "relative";

          // Indicatore speciale (stella in alto a destra)
          const adminIndicator = document.createElement("div");
          adminIndicator.style.position = "absolute";
          adminIndicator.style.top = "0";
          adminIndicator.style.right = "2px";
          adminIndicator.style.width = "0";
          adminIndicator.style.height = "0";
          adminIndicator.style.borderTop = "6px solid #3F51B5"; // Blu per admin/validator

          // Tooltip per dettagli
          container.title = `Valore approvato che verrà modificato da ${rowData.pendingChangeBy} (${bestRole})`;

          container.appendChild(valueSpan);
          container.appendChild(adminIndicator);

          return container;
        }
      }

      return value !== null && value !== undefined ? value : "";
    }

    // CASO 2: Se il record ha un valore pending per questa cella
    if (
      rowData.hasPendingChanges &&
      rowData.pendingValues &&
      rowData.pendingValues[field] !== undefined
    ) {
      const pendingValue = rowData.pendingValues[field];

      // Se il valore pending è diverso dal valore attuale
      if (String(pendingValue) !== String(value)) {
        // Crea un elemento div per contenere sia il valore attuale che quello pending
        const container = document.createElement("div");
        container.style.position = "relative";
        container.style.width = "100%";
        container.style.display = "flex";
        container.style.alignItems = "center";

        // Valore attuale (barrato)
        const currentValueSpan = document.createElement("span");
        currentValueSpan.textContent =
          value !== null && value !== undefined ? value : "-";
        currentValueSpan.style.textDecoration = "line-through";
        currentValueSpan.style.color = "#999";
        currentValueSpan.style.marginRight = "8px";

        // Valore pending (in evidenza)
        const pendingValueSpan = document.createElement("span");
        pendingValueSpan.textContent =
          pendingValue !== null && pendingValue !== undefined
            ? pendingValue
            : "-";
        pendingValueSpan.style.color =
          rowData.pendingChangeBy === user.email ? "#0D47A1" : "#B71C1C";
        pendingValueSpan.style.fontWeight = "bold";

        // Indicatore di modifica (triangolino giallo)
        const indicator = document.createElement("div");
        indicator.style.position = "absolute";
        indicator.style.top = "0";
        indicator.style.right = "2px";
        indicator.style.width = "0";
        indicator.style.height = "0";
        indicator.style.borderTop = "6px solid #FFC107";

        // Tooltip per dettagli sulla modifica
        container.title = `Valore originale: ${value || "-"}\nValore pending: ${pendingValue || "-"
          }\nModificato da: ${rowData.pendingChangeBy}`;

        container.appendChild(currentValueSpan);
        container.appendChild(pendingValueSpan);
        container.appendChild(indicator);

        return container;
      }
    }

    // CASO 3: Valore normale (nessuna modifica pending e non approvato)
    return value !== null && value !== undefined ? value : "";
  };

  const [columnHideDefault, setColumnHideDefault] = useState(['centroDiCosto_editable', 'voceDiCosto_editable', 'contoDiContropartita_frozen', 'desConto_frozen', 'documentoDiRiferimento_frozen', 'posizioneDocumentoDiRiferimento_frozen', 'contabilitSecond_frozen', 'fornitore_editable', 'descFornitore_editable', 'oda_editable', 'descOda_editable', 'collaudatoDa_frozen', 'descrizioneDocCO_editable']);

  let columns =
    [
      {
        formatter: "rowSelection",
        titleFormatter: "rowSelection",
        titleFormatterParams: { rowRange: "all" },
        hozAlign: "center",
        headerSort: false,
        width: 30,
        frozen: true,
        visible: true,
        cssClass: "frozen-column",
        columnType: "selection",
        cellClick: function (_, cell) {
          const row = cell.getRow();

          if (row.isSelected()) {
            row.deselect();
          } else {
            row.select();
          }
        },
      },
      {
        title: "Contratto",
        field: "contract",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: true, //!columnHideDefault.includes("contract_frozen"),
        columnType: "frozen",
        columnId: "contract_frozen",
      },
      {
        title: "Flusso",
        field: "flusso",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("flusso_frozen"),
        columnType: "frozen",
        columnId: "flusso_frozen",
      },
      {
        title: "SAP_CDC",
        field: "sapCDCOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapCDCOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapCDCOriginal_frozen",
      },
      {
        title: "CDC",
        field: "centroDiCosto",
        editor: "list",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        editorParams: {
          values: [],
          elementAttributes: {
            maxlength: "10",
          },
          verticalNavigation: "hybrid",
          sort: "asc",
          emptyValue: null,
          maxWidth: true,
          placeholderLoading: "Caricamento...",
          placeholderEmpty: "Nessun risultato",
          multiselect: false,
          filterDelay: 100,
          allowEmpty: true,
          listOnEmpty: true,
          freetext: true,
        },
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("centroDiCosto_editable"),
        headerFilter: "input",
        columnType: "editable",
        columnId: "centroDiCosto_editable",
      },
      {
        title: "Desc CDC",
        field: "descCdc",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("descCdc_frozen"),
        columnType: "frozen",
        columnId: "descCdc_frozen",
      },
      {
        title: "SAP_Conto",
        field: "sapContoOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapContoOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapContoOriginal_frozen",
      },
      {
        title: "Conto",
        field: "voceDiCosto",
        widthGrow: 1,
        editor: "list",
        sorter: "string",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("voceDiCosto_editable"),
        columnType: "editable",
        columnId: "voceDiCosto_editable",
      },
      {
        title: "Desc Conto",
        field: "descConto",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("descConto_frozen"),
        columnType: "frozen",
        columnId: "descConto_frozen",
      },
      {
        title: "Creatore Documento CO",
        field: "creatoreDocumentoCO",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("creatoreDocumentoCO_frozen"),
        columnType: "frozen",
        columnId: "creatoreDocumentoCO_frozen",
      },
      {
        title: "Conto di Contropartita",
        field: "contoDiContropartita",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("contoDiContropartita_frozen"),
        columnType: "frozen",
        columnId: "contoDiContropartita_frozen",
      },
      {
        title: "Des Conto Contropartita",
        field: "desConto",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("desConto_frozen"),
        columnType: "frozen",
        columnId: "desConto_frozen",
      },
      {
        title: "CO Document Number",
        field: "coDocumentNumber",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("coDocumentNumber_frozen"),
        columnType: "frozen",
        columnId: "coDocumentNumber_frozen",
      },
      {
        title: "Posizione Documento CO",
        field: "posizioneDocumentoCO",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("posizioneDocumentoCO_frozen"),
        columnType: "frozen",
        columnId: "posizioneDocumentoCO_frozen",
      },
      {
        title: "Documento di Riferimento",
        field: "documentoDiRiferimento",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("documentoDiRiferimento_frozen"),
        columnType: "frozen",
        columnId: "documentoDiRiferimento_frozen",
      },
      {
        title: "Posizione Doc Riferimento",
        field: "posizioneDocumentoDiRiferimento",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("posizioneDocumentoDiRiferimento_frozen"),
        columnType: "frozen",
        columnId: "posizioneDocumentoDiRiferimento_frozen",
      },
      {
        title: "Mese Fiscale",
        field: "periodo",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("periodo_frozen"),
        columnType: "frozen",
        columnId: "periodo_frozen",
      },
      {
        title: "Esercizio",
        field: "esercizio",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("esercizio_frozen"),
        columnType: "frozen",
        columnId: "esercizio_frozen",
      },
      {
        title: "Contabilità Second",
        field: "contabilitSecond",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("contabilitSecond_frozen"),
        columnType: "frozen",
        columnId: "contabilitSecond_frozen",
      },
      {
        title: "SAP_Fornitore",
        field: "sapFornitoreOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapFornitoreOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapFornitoreOriginal_frozen",
      },
      {
        title: "Fornitore",
        field: "fornitore",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("fornitore_editable"),
        columnType: "editable",
        columnId: "fornitore_editable",
      },
      {
        title: "SAP_DescFornitore",
        field: "sapDescFornitoreOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapDescFornitoreOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapDescFornitoreOriginal_frozen",
      },
      {
        title: "Desc Fornitore",
        field: "descFornitore",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("descFornitore_editable"),
        columnType: "editable",
        columnId: "descFornitore_editable",
      },
      {
        title: "Cliente",
        field: "cliente",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("cliente_frozen"),
        columnType: "frozen",
        columnId: "cliente_frozen",
      },
      {
        title: "Desc Cliente",
        field: "descCliente",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("descCliente_frozen"),
        columnType: "frozen",
        columnId: "descCliente_frozen",
      },
      {
        title: "SAP_Oda",
        field: "sapOdaOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapOdaOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapOdaOriginal_frozen",
      },
      {
        title: "Oda",
        field: "oda",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("oda_editable"),
        columnType: "editable",
        columnId: "oda_editable",
      },
      {
        title: "Posizione Oda",
        field: "posizioneOda",
        widthGrow: 1,
        editor: "input",
        sorter: "number",
        validator: ["required", "numeric"],
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("posizioneOda_frozen"),
        columnType: "frozen",
        columnId: "posizioneOda_frozen",
      },
      {
        title: "SAP_DescOda",
        field: "sapDescOdaOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapDescOdaOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapDescOdaOriginal_frozen",
      },
      {
        title: "Desc Oda",
        field: "descOda",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("descOda_editable"),
        columnType: "editable",
        columnId: "descOda_editable",
      },
      {
        title: "Buyer",
        field: "buyer",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("buyer_frozen"),
        columnType: "frozen",
        columnId: "buyer_frozen",
      },
      {
        title: "Collaudato Da",
        field: "collaudatoDa",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("collaudatoDa_frozen"),
        columnType: "frozen",
        columnId: "collaudatoDa_frozen",
      },
      {
        title: "Data Documento",
        field: "dataDocumento",
        widthGrow: 1,
        editor: "date",
        sorter: "date", // Cambia da "string" a "date"
        editable: false,
        frozen: true,
        headerFilter: "date", // Usa un input normale per la ricerca
        cssClass: "frozen-column",
        formatter: (cell) => formatDateInItalian(cell),
        visible: !columnHideDefault.includes("dataDocumento_frozen"),
        columnType: "frozen",
        columnId: "dataDocumento_frozen",
        headerFilterFunc: (headerValue, rowValue) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          const rowDate = new Date(rowValue).toLocaleDateString("it-IT");
          const filterDate = new Date(headerValue).toLocaleDateString("it-IT");

          return rowDate.includes(filterDate);
        },
      },
      {
        title: "Data Registrazione",
        field: "dataRegistrazione",
        widthGrow: 1,
        editor: "date",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "date",
        cssClass: "frozen-column",
        formatter: (cell) => formatDateInItalian(cell),
        visible: !columnHideDefault.includes("dataRegistrazione_frozen"),
        columnType: "frozen",
        columnId: "dataRegistrazione_frozen",
        headerFilterFunc: (headerValue, rowValue) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          const rowDate = new Date(rowValue).toLocaleDateString("it-IT");
          const filterDate = new Date(headerValue).toLocaleDateString("it-IT");

          return rowDate.includes(filterDate);
        },
      },
      {
        title: "Testo Documento CO",
        field: "testoDocumentoCO",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("testoDocumentoCO_frozen"),
        columnType: "frozen",
        columnId: "testoDocumentoCO_frozen",
      },
      {
        title: "SAP_DescrizioneDocCO",
        field: "sapDescDocCOOriginal",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sapDescDocCOOriginal_frozen"),
        columnType: "frozen",
        columnId: "sapDescDocCOOriginal_frozen",
      },
      {
        title: "Descrizione Doc CO",
        field: "descrizioneDocCO",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        formatter: combinedFormatter, // Usa il formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        headerFilter: "input",
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("descrizioneDocCO_editable"),
        columnType: "editable",
        columnId: "descrizioneDocCO_editable",
      },
      {
        title: "Euro",
        field: "euro",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        formatter: "money",
        formatterParams: {
          symbol: "€",
          precision: 2,
          thousand: ".",
          decimal: ",",
        },
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("euro_frozen"),
        columnType: "frozen",
        columnId: "euro_frozen",
      },
      {
        title: "Quantità",
        field: "quantita",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("quantita_frozen"),
        columnType: "frozen",
        columnId: "quantita_frozen",
      },
      {
        title: "SAP OU",
        field: "sap_OU",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("sap_OU_frozen"),
        columnType: "frozen",
        columnId: "sap_OU_frozen",
      },
      {
        title: "Local/Corporate",
        field: "corporate",
        widthGrow: 1,
        editor: "list",
        editable: false,
        headerFilter: "list",
        headerFilterParams: {
          values: ["", "Local", "Corporate"],
        },
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("corporate_frozen"),
        columnType: "frozen",
        columnId: "corporate_frozen",
      },
      {
        title: "Liv 1",
        field: "raggruppamento1",
        widthGrow: 1,
        editor: "list",
        editable: false,
        editorParams: {}, // Verrà popolato dinamicamente
        headerFilter: "list",
        headerFilterParams: {},
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("raggruppamento1_frozen"),
        columnType: "frozen",
        columnId: "raggruppamento1_frozen",
      },
      {
        title: "SAP_Liv2",
        field: "raggruppamento2Original",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("raggruppamento2Original_frozen"),
        columnType: "frozen",
        columnId: "raggruppamento2Original_frozen",
      },
      {
        title: "Liv 2",
        field: "raggruppamento2",
        widthGrow: 1,
        editor: "list",
        formatter: combinedFormatter,
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        editorParams: {}, // Verrà popolato dinamicamente
        headerFilter: "list",
        headerFilterParams: {},
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("raggruppamento2_editable"),
        columnType: "editable",
        columnId: "raggruppamento2_editable",
      },
      {
        title: "SAP_Liv3",
        field: "raggruppamento3Original",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        validator: "required",
        editable: false,
        frozen: true,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("raggruppamento3Original_frozen"),
        columnType: "frozen",
        columnId: "raggruppamento3Original_frozen",
      },
      {
        title: "Liv 3",
        field: "raggruppamento3",
        widthGrow: 1,
        editor: "list",
        formatter: combinedFormatter, // Uso del formatter combinato
        editable: function (cell) {
          const rowData = cell.getRow().getData();
          return (
            bestRole !== "Coordinator" &&
            (bestRole === "Admin" ||
              bestRole === "Validator" ||
              !rowData.recentlyApproved) && // Admin o Validator possono modificare anche record approvati
            (!rowData.hasPendingChanges || rowData.pendingChangeBy === user.email) // Nessuna modifica pendente o modifiche dell'utente corrente
          );
        },
        editorParams: {}, // Verrà popolato dinamicamente
        headerFilter: "list",
        headerFilterParams: {},
        cssClass: "editable-column",
        visible: !columnHideDefault.includes("raggruppamento3_editable"),
        columnType: "editable",
        columnId: "raggruppamento3_editable",
      },
      {
        title: "Responsabile",
        field: "responsabile",
        widthGrow: 1,
        editor: "input",
        sorter: "string",
        editable: false,
        headerFilter: "input",
        cssClass: "frozen-column",
        visible: !columnHideDefault.includes("responsabile_frozen"),
        columnType: "frozen",
        columnId: "responsabile_frozen",
      }
    ]

  // Refs
  const tableRef = useRef(null);

  const navigate = useNavigate();
  const { user, bestRole } = useContext(UserContext);

  // Main state
  const [table, setTable] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });

  // NUOVO: Stato per memorizzare i dati originali
  const [originalTableData, setOriginalTableData] = useState({});

  // Dialog states
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [filtersActive, setFiltersActive] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [searchBoxVisible, setSearchBoxVisible] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedRecordsCount, setSelectedRecordsCount] = useState(0);
  const [selectedRecordIds, setSelectedRecordIds] = useState("");

  // Record change states
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [changeHistory, setChangeHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    field: "",
  });
  const [lastHeight, setLastHeight] = useState(0);
  const [height, setHeight] = useState(0);
  const [loadingFetch, setLoadingFetch] = useState(false);

  // Funzione per il log di debugging
  const logRecordStatuses = () => {
    if (!table) return;

    const rows = table.getRows();

    let recentlyApprovedCount = 0;
    let pendingChangesCount = 0;

    rows.forEach((row) => {
      const data = row.getData();
      if (data.recentlyApproved === true) {
        recentlyApprovedCount++;

        // Controlla lo stato visivo delle celle editabili
        for (const field of [
          "centroDiCosto",
          "voceDiCosto",
          "fornitore",
          "descFornitore",
          "oda",
          "descOda",
          "descrizioneDocCO",
          "raggruppamento2",
          "raggruppamento3",
        ]) {
          const cell = row.getCell(field);
          if (cell) {
            const value = cell.getValue();
            const element = cell.getElement();
            const hasClass = element.classList.contains("approved-field");
          }
        }
      }

      if (data.hasPendingChanges) {
        pendingChangesCount++;
      }
    });
  };

  // Helper functions
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  // Funzione per caricare i valori pending nei record
  const loadPendingValuesIntoRecords = async () => {
    if (!table) return;

    try {
      // Ottieni tutte le modifiche pending
      const pendingRecordChanges = await OpexService.getPendingRecordChanges(onlyAllocation);

      // Filtra solo quelle con stato "pending"
      const pendingChanges = pendingRecordChanges.filter(
        (change) => change.status === "pending"
      );

      // Crea una mappa di modifiche pending per ID record
      const pendingRecordsMap = new Map();

      // Elabora ciascuna modifica pending
      pendingChanges.forEach((change) => {
        try {
          // Parse dei JSON delle modifiche
          const originalRecord = JSON.parse(change.originalRecordJson);
          const modifiedRecord = JSON.parse(change.modifiedRecordJson);

          // Trova i campi che sono stati modificati
          const pendingValues = {};
          Object.keys(modifiedRecord).forEach((key) => {
            if (
              JSON.stringify(modifiedRecord[key]) !==
              JSON.stringify(originalRecord[key])
            ) {
              pendingValues[key] = modifiedRecord[key];
            }
          });

          // Salva le informazioni sulle modifiche
          if (change.opexId) {
            pendingRecordsMap.set(change.opexId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
          if (change.opexOriginalId) {
            pendingRecordsMap.set(change.opexOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
        } catch (error) {
          console.error("Errore nel parsing dei JSON:", error);
        }
      });

      // Aggiorna le righe della tabella con i valori pending
      table.getRows().forEach((row) => {
        const rowData = row.getData();
        const recordId = rowData.id || rowData.original_Id;

        if (recordId && pendingRecordsMap.has(recordId)) {
          const pendingInfo = pendingRecordsMap.get(recordId);

          // Aggiorna il record con le informazioni sulle modifiche pending
          row.update({
            ...rowData,
            hasPendingChanges: true,
            pendingChangeBy: pendingInfo.modifiedBy,
            pendingValues: pendingInfo.pendingValues,
          });

          // Forza il ridisegno delle celle con valori pending per applicare il formatter
          for (const field in pendingInfo.pendingValues) {
            const cell = row.getCell(field);
            if (cell) {
              cell.getElement().classList.add("has-pending-value");
            }
          }
        }
      });

      // Forza un ridisegno della tabella per applicare tutti i formattatori
      table.redraw(true);
    } catch (error) {
      console.error("Errore nel caricamento dei valori pending:", error);
    }
  };

  const fetchData = async () => {
    setLoadingFetch(true);
    try {
      const response = await OpexService.getOpexes(onlyAllocation);
      if (table) {
        // NUOVO: Memorizza i dati originali in un oggetto indicizzato per ID
        const originalDataMap = {};
        response.forEach((record) => {
          // Normalizza i valori null e undefined in stringhe vuote per i campi editabili
          const normalizedRecord = { ...record };
          [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ].forEach((key) => {
            if (
              normalizedRecord[key] === null ||
              normalizedRecord[key] === undefined
            ) {
              normalizedRecord[key] = "";
            }
          });
          originalDataMap[record.id] = normalizedRecord;
        });
        let columnHideDefault_ = [...columnHideDefault];
        for (let i = 0; i < response.length; i++) {
          if(columnHideDefault_.includes("dataDocumento_frozen"))
            break;
          if (response[i].dataDocumento === response[i].dataRegistrazione) {
            let visibleColumns_ = {...visibleColumns}
            visibleColumns_["dataDocumento_frozen"] = false;
            setVisibleColumns(visibleColumns_);
            columnHideDefault_.push("dataDocumento_frozen");
            setColumnHideDefault(columnHideDefault_)
            break;
          }
        }

        // 
        setOriginalTableData(originalDataMap);

        table.setData(response);

        // Carica e applica i valori pending dopo il caricamento dei dati
        await loadPendingValuesIntoRecords();
        setLoadingFetch(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
      setLoadingFetch(false);
    }
  };

  const loadPendingChangesCount = async () => {
    try {
      const recordLevelChanges = await OpexService.getPendingRecordChanges(onlyAllocation);
      setPendingChangesCount(recordLevelChanges.length);
      return recordLevelChanges.length;
    } catch (error) {
      console.error("Error loading pending changes count:", error);
      showNotification("Error loading pending changes count", "error");
      return 0;
    }
  };

  const loadChangeHistory = async () => {
    try {
      const recordLevelHistory = await OpexService.getRecordChangeHistory(
        historyFilters
      );
      setChangeHistory(recordLevelHistory);
    } catch (error) {
      console.error("Error loading change history:", error);
      showNotification("Error loading history", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, `${title} Data`);
      XLSX.writeFile(wb, `${title}_data.xlsx`);
    }
  };

  const handleColumnVisibilityChange = (columnId, isVisible, field, type) => {
    if (table) {
      const column = table.getColumn(field);
      if (column) {
        isVisible ? column.show() : column.hide();
        setVisibleColumns((prev) => ({ ...prev, [columnId]: isVisible }));
      }
    }
  };

  const handlePendingChangesClick = () => {
    navigate(pendingHref);
  };

  const handleHistoryClick = () => {
    navigate(historyHref);
  };

  const handleSearch = ({
    contract,
    esercizio,
    conto,
    cdc,
    meseFiscale,
    oda,
    buyer,
  }) => {
    if (!table) return;

    table.clearFilter();

    // Esci se tutti i parametri sono vuoti
    if (
      !contract &&
      !esercizio &&
      !conto &&
      !cdc &&
      !meseFiscale &&
      !oda &&
      !buyer
    ) {
      return;
    }

    const filters = [];

    // Filtri esistenti
    if (contract) {
      filters.push({
        field: "contract",
        type: "like",
        value: contract,
      });
    }

    if (esercizio) {
      filters.push({
        field: "esercizio",
        type: "like",
        value: esercizio,
      });
    }

    if (conto) {
      filters.push({
        field: "voceDiCosto", // Campo effettivo nel database
        type: "like",
        value: conto,
      });
    }

    if (cdc) {
      filters.push({
        field: "centroDiCosto", // Campo effettivo nel database
        type: "like",
        value: cdc,
      });
    }

    if (meseFiscale) {
      filters.push({
        field: "periodo", // Campo effettivo nel database per "Mese Fiscale"
        type: "like",
        value: meseFiscale,
      });
    }

    if (oda) {
      filters.push({
        field: "oda", // Campo effettivo nel database
        type: "like",
        value: oda,
      });
    }

    // Nuovo filtro per buyer
    if (buyer) {
      filters.push({
        field: "buyer", // Campo effettivo nel database
        type: "like",
        value: buyer,
      });
    }

    if (filters.length > 0) {
      table.setFilter(filters);

      // Costruisci il messaggio di notifica aggiornato
      const appliedFilters = [
        contract ? `Contract = ${contract}` : "",
        esercizio ? `Esercizio = ${esercizio}` : "",
        conto ? `Conto = ${conto}` : "",
        cdc ? `CDC = ${cdc}` : "",
        meseFiscale ? `Mese Fiscale = ${meseFiscale}` : "",
        oda ? `Oda = ${oda}` : "",
        buyer ? `Buyer = ${buyer}` : "", // Nuovo campo nel messaggio
      ].filter(Boolean);

      showNotification(
        `Ricerca applicata: ${appliedFilters.join(", ")}`,
        "default"
      );
    }
  };

  const disableEditingForRecordsWithPendingChanges = async () => {
    if (!table) return;

    try {
      // Get all pending changes
      const pendingRecordChanges = await OpexService.getPendingRecordChanges(onlyAllocation);

      // Filter only those with "pending" status
      const pendingChanges = pendingRecordChanges.filter(
        (change) => change.status === "pending"
      );

      // Create a map associating record ID with its modifier and values
      const pendingRecordsMap = new Map();

      pendingChanges.forEach((change) => {
        try {
          // Parse dei JSON delle modifiche
          const originalRecord = JSON.parse(change.originalRecordJson);
          const modifiedRecord = JSON.parse(change.modifiedRecordJson);

          // Trova i campi che sono stati modificati
          const pendingValues = {};
          Object.keys(modifiedRecord).forEach((key) => {
            if (
              JSON.stringify(modifiedRecord[key]) !==
              JSON.stringify(originalRecord[key])
            ) {
              pendingValues[key] = modifiedRecord[key];
            }
          });

          if (change.opexId) {
            pendingRecordsMap.set(change.opexId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
          if (change.opexOriginalId) {
            pendingRecordsMap.set(change.opexOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: pendingValues,
            });
          }
        } catch (error) {
          console.error("Errore nel parsing dei JSON:", error);

          // Fallback al comportamento originale in caso di errore
          if (change.opexId) {
            pendingRecordsMap.set(change.opexId, {
              modifiedBy: change.modifiedBy,
              pendingValues: {},
            });
          }
          if (change.opexOriginalId) {
            pendingRecordsMap.set(change.opexOriginalId, {
              modifiedBy: change.modifiedBy,
              pendingValues: {},
            });
          }
        }
      });

      // Process all rows
      table.getRows().forEach((row) => {
        const rowData = row.getData();
        const recordId = rowData.id || rowData.original_Id;

        // Ottieni le informazioni sulla modifica pending per questo record
        const pendingInfo = pendingRecordsMap.get(recordId);
        const pendingChangeBy = pendingInfo ? pendingInfo.modifiedBy : null;
        const pendingValues = pendingInfo ? pendingInfo.pendingValues : null;

        // Verifica se il record è stato approvato
        const isRecentlyApproved = rowData.recentlyApproved === true;

        // Se il record è stato approvato, applica stile speciale
        if (isRecentlyApproved) {
          // Aggiungi classe e stili alla riga
          const rowElement = row.getElement();
          rowElement.classList.add("recently-approved-row");
          rowElement.style.backgroundColor = "#F1F8E9"; // Verde molto chiaro per la riga
          rowElement.setAttribute("data-approved", "true");

          const cells = rowElement.querySelectorAll(".tabulator-cell");
          if (cells.length > 0) {
            const selectionCell = cells[0]; // La prima cella è la colonna di selezione
            selectionCell.style.backgroundColor = "#2E7D32"; // Verde scuro
            selectionCell.style.color = "#FFFFFF"; // Testo bianco per migliorare contrasto
          }

          // Applica lo stile a ciascuna cella modificabile
          for (const field of [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();
              // Style differently for admin and validator users
              if (bestRole === "Admin" || bestRole === "Validator") {
                // Per gli admin o validator, usa uno stile che indica che è approvato ma modificabile
                cellElement.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                cellElement.style.color = "#2E7D32"; // Verde scuro
                cellElement.style.border = "1px dashed #81C784"; // Bordo tratteggiato per indicare che è modificabile
                cellElement.title = `Questo campo è stato approvato. Come ${bestRole}, puoi comunque modificarlo.`;
                cellElement.classList.add("approved-field-admin-editable");
              } else {
                // Per gli altri utenti, mantieni lo stile originale
                cellElement.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                cellElement.style.color = "#2E7D32"; // Verde scuro
                cellElement.style.border = "1px solid #81C784"; // Bordo verde
                cellElement.title =
                  "Questo campo è stato approvato e non può essere modificato";
                cellElement.classList.add("approved-field");
              }

              // IMPORTANTE: Assicurati che il contenuto della cella sia visibile
              const value = cell.getValue();
              if (value !== null && value !== undefined && value !== "") {
                cellElement.setAttribute("data-value", value);
                cellElement.setAttribute(
                  "aria-label",
                  bestRole === "Admin" || bestRole === "Validator"
                    ? `Campo approvato modificabile: ${value}`
                    : `Campo approvato: ${value}`
                );
              }
            }
          }
        }

        // If we found pending changes for this record
        if (pendingChangeBy) {
          // Flag the record as having pending changes
          row.update(
            {
              hasPendingChanges: true,
              pendingChangeBy: pendingChangeBy,
              pendingValues: pendingValues || {},
            },
            true
          );

          // Highlight the row using direct style instead of classes
          const rowElement = row.getElement();
          rowElement.style.backgroundColor = "#FFF8E1"; // Light yellow background
          rowElement.setAttribute("data-modified-by", pendingChangeBy);

          const cells = rowElement.querySelectorAll(".tabulator-cell");
          if (cells.length > 0) {
            const selectionCell = cells[0];
            selectionCell.style.backgroundColor = "#ffd700"; // Giallo per selezione
            selectionCell.style.color = "#000000"; // Testo nero per contrasto
          }

          // Process each editable cell
          for (const field of [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();

              // Verifica se questo campo specifico ha un valore pending
              const hasPendingValue =
                pendingValues && pendingValues[field] !== undefined;

              if (pendingChangeBy === user.email) {
                // If changes are from current user, highlight but DON'T disable
                cellElement.style.backgroundColor = hasPendingValue
                  ? "#E3F2FD"
                  : ""; // Light blue for own changes
                cellElement.style.color = hasPendingValue ? "#0D47A1" : ""; // Darker blue text
                cellElement.title = hasPendingValue
                  ? `Hai modificato questo valore da ${rowData[field]} a ${pendingValues[field]}`
                  : "Hai già inviato modifiche pendenti per questo record. Puoi continuare a modificarlo.";
              } else {
                // If changes are from another user, disable
                cellElement.style.backgroundColor = hasPendingValue
                  ? "#FFEBEE"
                  : ""; // Light red for others' changes
                cellElement.style.color = hasPendingValue ? "#B71C1C" : ""; // Darker red text
                cellElement.title = hasPendingValue
                  ? `${pendingChangeBy} ha modificato questo valore da ${rowData[field]} a ${pendingValues[field]}`
                  : `Questo record ha modifiche pendenti inviate da ${pendingChangeBy}`;
              }

              if (hasPendingValue) {
                cellElement.setAttribute(
                  "data-pending-value",
                  pendingValues[field]
                );
              }
              cellElement.setAttribute("data-modified-by", pendingChangeBy);
            }
          }

          // Forza il ridisegno per applicare il formatter che mostra i valori pending
          row.reformat();
        } else if (!isRecentlyApproved) {
          // Reimposta se il record non ha più modifiche pendenti E non è approvato
          row.update(
            {
              hasPendingChanges: false,
              pendingChangeBy: null,
              pendingValues: null,
            },
            true
          );

          // Rimuovi gli stili
          const rowElement = row.getElement();
          rowElement.style.backgroundColor = ""; // Reset background
          rowElement.removeAttribute("data-modified-by");
          rowElement.classList?.remove("recently-approved-row");
          rowElement.removeAttribute("data-approved");

          for (const field of [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ]) {
            const cell = row.getCell(field);
            if (cell) {
              const cellElement = cell.getElement();
              cellElement.style.backgroundColor = ""; // Reset background
              cellElement.style.color = ""; // Reset text color
              cellElement.style.border = ""; // Reset border
              cellElement.title = "";
              cellElement.classList?.remove("approved-field");
              cellElement.removeAttribute("data-modified-by");
              cellElement.removeAttribute("data-value");
              cellElement.removeAttribute("data-pending-value");
              cellElement.removeAttribute("aria-label");
            }
          }
        }
      });

      // Forza un ridisegno della tabella per assicurarsi che tutti i formatter siano applicati
      table.redraw(false);
    } catch (error) {
      console.error("Error checking records with pending changes:", error);
    }
  };

  const handleApproveSelectedRecords = () => {
    if (!table) return;

    // Ottieni le righe selezionate
    const selectedRows = table.getSelectedRows();

    if (selectedRows.length === 0) {
      showNotification("Seleziona almeno un record da approvare", "warning");
      return;
    }

    // Estrai gli ID dei record selezionati e formattali come stringa CSV
    const selectedIds = selectedRows.map((row) => row.getData().id);
    const idsCsv = selectedIds.join(",");

    // Salva i dati e apri la dialog di conferma
    setSelectedRecordsCount(selectedIds.length);
    setSelectedRecordIds(idsCsv);
    setConfirmDialogOpen(true);
  };

  const confirmApproval = async () => {
    try {
      // Chiudi la dialog
      setConfirmDialogOpen(false);

      // Chiama il servizio per approvare i record
      await OpexService.setOpexRecentlyApproved(selectedRecordIds);

      showNotification(
        `${selectedRecordsCount} record approvati con successo`,
        "success"
      );

      // Ricarica i dati della tabella per aggiornare lo stato
      await fetchData();

      // Aggiorna la visualizzazione
      await disableEditingForRecordsWithPendingChanges();
    } catch (error) {
      console.error("Errore nell'approvazione dei record:", error);
      showNotification("Errore durante l'approvazione dei record", "error");
    }
  };

  const toggleSearchBox = () => {
    setSearchBoxVisible(!searchBoxVisible);
  };

  // Effect to load the user role at startup
  useEffect(() => {
    if (table) {
      const tableElement = table.element;
      tableElement.classList.add("hide-filters");
    }
  }, [table]);

  // Effect for initial data loading
  useEffect(() => {
    if (table && onlyAllocation !== undefined) {
      fetchData().then(() => {
        // Call after data is loaded
        disableEditingForRecordsWithPendingChanges();
      });
    }
  }, [table, onlyAllocation]);

  useEffect(() => {
    if (table && cdcList.length > 0 && accountsList.length > 0) {
      table.getColumn("centroDiCosto").updateDefinition({
        editorParams: {
          autocomplete: true,
          values: cdcList.map((c) => c.cdc),
          displayField: "label",
          valueField: "value",
          filterFunc: function (term, label) {
            return label?.toLowerCase().includes(term?.toLowerCase());
          },
        },
      });
      table.getColumn("voceDiCosto").updateDefinition({
        editorParams: {
          autocomplete: true,
          values: accountsList.map((a) => a.conto),
          displayField: "label",
          valueField: "value",
          filterFunc: function (term, label) {
            return label?.toLowerCase().includes(term?.toLowerCase());
          },
        },
      });
    }
  }, [table, cdcList, accountsList]);

  useEffect(() => {
    LevelOpexService.getAccountsConfiguration().then((data) => {
      setAccountsList(data);
    });
    LevelOpexService.getCDCConfigurations().then((data) => {
      setCdcList(data);
    });
    LevelOpexService.getExpenseItemsConfigurations().then((data) => {
      expenseItemsConfigurations.current = data;
    });
  }, []);

  // Call after pending changes count is updated
  useEffect(() => {
    if (table) {
      disableEditingForRecordsWithPendingChanges();
    }
  }, [pendingChangesCount]);

  // Updating the count of pending changes
  useEffect(() => {
    if (table && (bestRole === "Admin" || bestRole === "Validator")) {
      const updatePendingChangesCount = async () => {
        await loadPendingChangesCount();
      };

      updatePendingChangesCount();

      // Update the count every minute
      const intervalId = setInterval(updatePendingChangesCount, 60000);

      return () => clearInterval(intervalId);
    }
  }, [table, bestRole]);

  // Effect for table setup
  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      } 
      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitColumns",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        initialSort: [{ column: "contract", dir: "desc" }],
        rowHeight: 40, 
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: true,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        navigationMode: "cell",
        clipboard: true,
        editTriggerEvent: "dblclick",
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
        // Callback per la renderizzazione delle righe
        rowFormatter: function (row) {
          const rowData = row.getData();

          // Se il record è stato approvato, applicagli uno stile speciale
          if (rowData.recentlyApproved === true) {
            const rowElement = row.getElement();
            rowElement.classList.add("recently-approved-row");
            rowElement.style.backgroundColor = "#F1F8E9"; // Verde chiarissimo

            // Aggiungi attributo data-approved per facilitare il targeting CSS
            rowElement.setAttribute("data-approved", "true");

            const cells = rowElement.querySelectorAll(".tabulator-cell");
            if (cells.length > 0) {
              const selectionCell = cells[0]; // La prima cella è la colonna di selezione
              selectionCell.style.backgroundColor = "#2E7D32"; // Verde scuro
              selectionCell.style.color = "#FFFFFF"; // Testo bianco per migliorare contrasto
            }
          } else if (rowData.hasPendingChanges) {
            const rowElement = row.getElement();
            rowElement.style.backgroundColor = "#FFF8E1"; // Sfondo giallo chiaro

            const cells = rowElement.querySelectorAll(".tabulator-cell");
            if (cells.length > 0) {
              const selectionCell = cells[0]; // La prima cella è la colonna di selezione
              selectionCell.style.backgroundColor = "#ffd700"; // Giallo brillante
              selectionCell.style.color = "#000000"; // Testo nero per contrasto
            }
          }
        },
        // Callback per la renderizzazione delle celle
        cellFormatter: function (cell) {
          const rowData = cell.getRow().getData();
          const field = cell.getField();

          // Applica stili speciali solo alle colonne editabili
          if (
            [
              "centroDiCosto",
              "voceDiCosto",
              "fornitore",
              "descFornitore",
              "oda",
              "descOda",
              "descrizioneDocCO",
              "raggruppamento2",
              "raggruppamento3",
            ].includes(field)
          ) {
            if (rowData.recentlyApproved === true) {
              const element = cell.getElement();

              if (bestRole === "Admin" || bestRole === "Validator") {
                // Per gli admin e validator, usa uno stile che indica che è approvato ma modificabile
                element.classList.add("approved-field-admin-editable");
                element.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                element.style.color = "#2E7D32"; // Verde scuro
                element.style.border = "1px dashed #81C784"; // Bordo tratteggiato per indicare che è modificabile
                element.title = `Questo campo è stato approvato. Come ${bestRole}, puoi comunque modificarlo.`;
              } else {
                // Per gli altri utenti, mantieni lo stile originale
                element.classList.add("approved-field");
                element.style.backgroundColor = "#E8F5E9"; // Verde chiaro
                element.style.color = "#2E7D32"; // Verde scuro
                element.title =
                  "Questo campo è stato approvato e non può essere modificato";
              }

              // Assicurati che il valore sia visibile
              const value = cell.getValue();
              if (value !== null && value !== undefined) {
                element.setAttribute("data-value", value);
              }
            }
          }
        },
        // Callback dopo il caricamento dei dati
        dataLoaded: function (data) {
          // Controlla ogni riga per record approvati dopo il caricamento dei dati
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
            loadPendingValuesIntoRecords(); // Aggiungi qui il caricamento dei valori pending
          }, 100);
        },
        dataSorted: function () {
          // Riapplica gli stili quando l'ordinamento cambia
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        },
        dataFiltered: function () {
          // Riapplica gli stili quando il filtro cambia
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        },
        pageLoaded: function () {
          // Riapplica gli stili quando si cambia pagina
          setTimeout(() => {
            disableEditingForRecordsWithPendingChanges();
          }, 100);
        },
      });

      setTable(newTable);

      // Initialize column visibility
      const initialVisibility = {};
      columns.forEach((col) => {
        if (col.columnId) {
          initialVisibility[col.columnId] = col.visible;
        }
      });
      setVisibleColumns(initialVisibility);

      return () => {
        if (newTable) {
          newTable.destroy();
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  // Handlers for cell clicks and double clicks (for edit functionality)
  useEffect(() => {
    if (table) {
      // Clean up previous handlers
      table.off("cellClick");
      table.off("cellDblClick");

      // When a cell is clicked
      table.on("cellClick", function (e, cell) {
        const rowData = cell.getRow().getData();
        const field = cell.getField();

        // Only check for editable columns
        if (
          [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            e.stopPropagation();
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            e.stopPropagation();
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false;
          } else if (
            rowData.recentlyApproved === true &&
            (bestRole === "Admin" || bestRole === "Validator")
          ) {
            // Optional: notify admin/validator they're editing an approved record
            showNotification(
              `Stai modificando un record approvato (permesso speciale ${bestRole})`,
              "default"
            );
          }

          // Block if there are pending changes by others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user.email
          ) {
            e.stopPropagation();
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false;
          }
        }
      });

      // Double click handler (important for edit triggering)
      table.on("cellDblClick", function (e, cell) {
        const rowData = cell.getRow().getData();
        const field = cell.getField();
        // Only for editable columns

        if (
          [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            e.stopPropagation();
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            e.stopPropagation();
            e.preventDefault(); // Prevent default double-click behavior
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false;
          }

          // Block if there are pending changes by others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user.email
          ) {
            e.stopPropagation();
            e.preventDefault(); // Prevent default double-click behavior
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false;
          }
        }
      });
    }
  }, [table, user?.email, bestRole]);

  // Add an explicit check in the cellEditing event
  useEffect(() => {
    if (table) {
      table.off("cellEditing");

      table.on("cellEditing", function (cell) {
        const rowData = cell.getRow().getData();
        const field = cell.getField();

        if (field === "raggruppamento2") {
          const options = [
            ...new Set(
              expenseItemsConfigurations.current
                .filter(
                  (el) =>
                    el.conto === rowData.voceDiCosto &&
                    el.cdc === rowData.centroDiCosto
                )
                .map((el) => el.liv2)
            ),
          ];

          cell.getColumn()._column.definition.editorParams.values = options;
        }

        if (field === "raggruppamento3") {
          const options = [
            ...new Set(
              expenseItemsConfigurations.current
                .filter(
                  (el) =>
                    el.conto === rowData.voceDiCosto &&
                    el.cdc === rowData.centroDiCosto
                )
                .map((el) => el.liv3)
            ),
          ];

          cell.getColumn()._column.definition.editorParams.values = options;
        }

        // Only for our editable columns
        if (
          [
            "centroDiCosto",
            "voceDiCosto",
            "fornitore",
            "descFornitore",
            "oda",
            "descOda",
            "descrizioneDocCO",
            "raggruppamento2",
            "raggruppamento3",
          ].includes(field)
        ) {
          // NUOVO: Blocca se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "warning"
            );
            return false; // Blocca l'editing
          }

          // Block ONLY if there are pending changes from others
          if (
            rowData.hasPendingChanges &&
            rowData.pendingChangeBy !== user?.email
          ) {
            showNotification(
              `Questo record ha modifiche pendenti inviate da ${rowData.pendingChangeBy}`,
              "warning"
            );
            return false; // Blocks editing
          }
        }

        // Allow editing otherwise
        return true;
      });

      return () => {
        if (table) {
          table.off("cellEditing");
        }
      };
    }
  }, [table, user?.email, bestRole]);

  useEffect(() => {
    if (table) {
      table.off("cellEdited");

      table.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row.getData();
          const oldValue = cell.getOldValue();
          const newValue = cell.getValue();
          const field = cell.getField();

          // NUOVO: Verifica se il record è stato approvato (tranne per admin e validator)
          if (bestRole === "Coordinator") {
            showNotification(
              "Il ruolo Coordinator può solo visualizzare i dati, non modificarli",
              "warning"
            );
            return false;
          }
          if (
            rowData.recentlyApproved === true &&
            bestRole !== "Admin" &&
            bestRole !== "Validator"
          ) {
            showNotification(
              "Questo record è stato approvato e non può essere modificato",
              "error"
            );
            cell.setValue(oldValue); // Ripristina il valore originale
            return;
          }

          if (!rowData.id || rowData.id <= 0) {
            showNotification(`Error: Invalid ${title} ID`, "error");
            cell.setValue(oldValue);
            return;
          }

          // Check for pending changes by other users
          try {
            const pendingChanges = await OpexService.getPendingRecordChanges(onlyAllocation);

            // Look for pending changes for this record FROM OTHER USERS
            const existingChanges = pendingChanges.filter(
              (change) =>
                (change.opexId === rowData.id ||
                  change.opexOriginalId === rowData.original_Id) &&
                change.status === "pending" &&
                change.modifiedBy !== user.email
            );

            if (existingChanges.length > 0) {
              // Show message for changes by other users
              const latestChange = existingChanges.reduce((latest, current) => {
                return new Date(current.modifiedAt) >
                  new Date(latest.modifiedAt)
                  ? current
                  : latest;
              }, existingChanges[0]);

              showNotification(
                `Questo record ha modifiche pendenti inviate da ${latestChange.modifiedBy}`,
                "error"
              );
              cell.setValue(oldValue); // Restore original value

              // Refresh the display of pending changes
              await disableEditingForRecordsWithPendingChanges();
              return;
            }
          } catch (error) {
            console.error("Error checking pending changes:", error);
          }

          if (field === "centroDiCosto") {
            const selectedCDC = cdcList.find((c) => c.cdc === newValue) ?? null;
            if (selectedCDC) {
              await row.update({
                descCdc: selectedCDC.desc_CDC,
                corporate: selectedCDC.local_Corporate,
                responsabile: selectedCDC.responsabile,
              });
            }
          } else if (field === "voceDiCosto") {
            const conto =
              accountsList.find((c) => c.conto === newValue) ?? null;
            if (conto) {
              await row.update({
                raggruppamento1: conto.level1Opex,
                descConto: conto.desc_IT,
              });
            }
          }

          // NUOVO: Utilizzo di originalTableData per mantenere i dati originali
          const trueOriginalData = originalTableData[rowData.id];

          if (!trueOriginalData) {
            console.error(
              `Nessun dato originale trovato per il record ID: ${rowData.id}`
            );
            showNotification("Errore: dati originali mancanti", "error");
            cell.setValue(oldValue);
            return;
          }

          // NUOVO: Otteniamo le modifiche pendenti esistenti per questo record
          try {
            const pendingChanges = await OpexService.getPendingRecordChanges(onlyAllocation);
            const existingChange = pendingChanges.find(
              (change) =>
                (change.opexId === rowData.id ||
                  change.opexOriginalId === rowData.original_Id) &&
                change.status === "pending" &&
                change.modifiedBy === user.email
            );

            // Crea modifiedData partendo dai dati originali
            const modifiedData = { ...trueOriginalData };

            // Se ci sono modifiche pendenti esistenti, applicale prima di aggiungere la nuova modifica
            if (existingChange) {
              try {
                const pendingModifiedRecord = JSON.parse(
                  existingChange.modifiedRecordJson
                );

                // Trova i campi modificati e applicali a modifiedData
                Object.keys(pendingModifiedRecord).forEach((key) => {
                  // Ignora proprietà speciali di gestione
                  if (
                    key === "pendingValues" ||
                    key === "pendingChangeBy" ||
                    key === "hasPendingChanges"
                  ) {
                    return;
                  }

                  if (
                    JSON.stringify(pendingModifiedRecord[key]) !==
                    JSON.stringify(trueOriginalData[key])
                  ) {
                    modifiedData[key] = pendingModifiedRecord[key];
                  }
                });
              } catch (error) {
                console.error(
                  "Errore nel parsing delle modifiche esistenti:",
                  error
                );
              }
            }

            // Poi applica la modifica corrente
            modifiedData[field] = newValue;

            // Propaga i campi derivati nel payload (stessa logica dell'aggiornamento UI)
            if (field === "voceDiCosto") {
              const conto = accountsList.find((c) => c.conto === newValue) ?? null;
              if (conto) {
                modifiedData.raggruppamento1 = conto.level1Opex;
                modifiedData.descConto = conto.desc_IT;
              }
            } else if (field === "centroDiCosto") {
              const selectedCDC = cdcList.find((c) => c.cdc === newValue) ?? null;
              if (selectedCDC) {
                modifiedData.descCdc = selectedCDC.desc_CDC;
                modifiedData.corporate = selectedCDC.local_Corporate;
                modifiedData.responsabile = selectedCDC.responsabile;
              }
            }

            // Rimuovi le proprietà di gestione per sicurezza
            delete modifiedData.pendingValues;
            delete modifiedData.pendingChangeBy;
            delete modifiedData.hasPendingChanges;

            // Normalizza i valori per i campi editabili per garantire consistenza
            [
              "centroDiCosto",
              "voceDiCosto",
              "fornitore",
              "descFornitore",
              "oda",
              "descOda",
              "descrizioneDocCO",
              "raggruppamento2",
              "raggruppamento3",
            ].forEach((key) => {
              if (
                trueOriginalData[key] === null ||
                trueOriginalData[key] === undefined
              ) {
                trueOriginalData[key] = "";
              }

              if (
                modifiedData[key] === null ||
                modifiedData[key] === undefined
              ) {
                modifiedData[key] = "";
              }
            });

            // Aggiorna anche il valore pending nella UI immediatamente
            // Prima ottieni tutte le modifiche pendenti esistenti
            const existingPendingValues = { ...(rowData.pendingValues || {}) };
            existingPendingValues[field] = newValue;

            row.update({
              ...rowData,
              pendingValues: existingPendingValues,
              pendingChangeBy: user.email,
              hasPendingChanges: true,
            });

            // Send the pending change to the server
            try {
              const payload = {
                opexId: rowData.id,
                contract: String(rowData.contract || ""),
                opexOriginalId: rowData.original_Id,
                originalRecordJson: JSON.stringify(trueOriginalData),
                modifiedRecordJson: JSON.stringify(modifiedData),
                modifiedBy: user.email,
              };

              await OpexService.insertPendingRecordChange(payload);
              await loadPendingChangesCount(); // Update the count after adding

              // Forza il ridisegno della cella per mostrare il valore pending
              cell.getRow().reformat();

              showNotification(
                "Modifica salvata e in attesa di approvazione",
                "default"
              );
            } catch (error) {
              console.error("Error submitting pending change:", error);
              cell.setValue(oldValue);

              if (error.response?.data?.detail) {
                showNotification(
                  `Error: ${error.response.data.detail}`,
                  "error"
                );
              } else {
                showNotification(
                  "Errore nell'invio della modifica pendente",
                  "error"
                );
              }
            }
          } catch (error) {
            console.error(
              "Errore nel recuperare le modifiche pendenti:",
              error
            );
            showNotification(
              "Errore nel recuperare le modifiche pendenti",
              "error"
            );
            cell.setValue(oldValue);
            return;
          }
        } catch (error) {
          console.error("Error handling edit:", error);

          if (error.response?.data?.detail) {
            showNotification(`Error: ${error.response.data.detail}`, "error");
          } else {
            showNotification("Errore nella gestione della modifica", "error");
          }
        }
      });
    }
  }, [table, user.email, originalTableData, bestRole, cdcList, accountsList]);

  useEffect(() => {
    if (table && columnHideDefault?.length > 0) {  
      columnHideDefault.forEach((col) => {
        let index = columns.findIndex(x => x.columnId === col)
        if(index > -1){
          let column = table.getColumn(columns[index].field);
          column.hide();
        }
      });
    }
  }, [table, columnHideDefault]);

  const updateHeight = (filtersActive) => {
    if (document.getElementsByClassName("OPEX_container").length > 0) {
      let height_ = document.getElementsByClassName("OPEX_container")[0].clientHeight;
      if(filtersActive) {
        height_ += 20; // Aggiungo spazio extra se i filtri sono attivi
      }
      setHeight(height_);
    }
  }; 
  
  useEffect(() => { 
    // Aggiungo l’event listener al resize della finestra
    window.addEventListener("resize", updateHeight);
    document.body.classList.add("noOverflowVertical");
    window.scrollTo(0, 0);
    // Cleanup per evitare memory leaks
    return () => {
      window.removeEventListener("resize", updateHeight);
      document.body.classList.remove("noOverflowVertical");
    };
  }, []);  

  useEffect(() => {
    if(!loadingFetch){
      updateHeight(filtersActive);
    }
  }, [searchBoxVisible, filtersActive, loadingFetch]);

  useEffect(() => {
    if (tableRef.current && table && height > 0) {
      table.setHeight(`calc(100vh - ${height + 110}px)`);
    }
  }, [table, height]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>

      <div className="OPEX_container">
        <Q8Breadcrumbs
          links={[{ label: title, href: href }]}
          variant="primary"
        />

        <Box
          sx={{
            mt: 1,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" sx={{ color: "primary.main" }}>
              Tabella {title}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "left", gap: 0.5 }}>
            {/* 1. Colonne visibili */}
            <Q8Button
              variant="secondary"
              label="Colonne Visibili"
              icon={<ColumnIcon />}
              onClick={() => setColumnSelectorOpen(true)}
              size="small"
            />

            {/* 2. Mostra ricerca */}
            <Q8Button
              variant="secondary"
              label={searchBoxVisible ? "Nascondi Ricerca" : "Mostra Ricerca"}
              icon={<SearchIcon />}
              onClick={toggleSearchBox}
              size="small"
            />

            {/* 3. Mostra filtri */}
            <Q8Button
              variant="tertiary"
              label={filtersActive ? "Nascondi Filtri" : "Mostra Filtri"}
              icon={<FilterIcon />}
              onClick={() => {
                if (table) {
                  const newState = !filtersActive;
                  setFiltersActive(newState);

                  const tableElement = table.element;
                  if (newState) {
                    tableElement.classList?.remove("hide-filters");
                  } else {
                    tableElement.classList.add("hide-filters");
                    table.getColumns().forEach((column) => {
                      if (column.getField() && column.getHeaderFilterValue()) {
                        column.setHeaderFilterValue("");
                      }
                    });
                  }
                }
              }}
              size="small"
            />

            {/* 4. Scarica excel */}
            <Q8Button
              variant="secondary"
              label="Esporta Excel"
              icon={<ExportIcon />}
              onClick={handleDownloadExcel}
              size="small"
            />

            {/* Pulsanti visibili solo per Admin/Validator */}
            {(bestRole === "Admin" || bestRole === "Validator") && (
              <>
                {/* 5. Approva record selezionati */}
                <Q8Button
                  variant="secondary"
                  label="Approva Record Selezionati"
                  icon={<ApproveIcon />}
                  onClick={handleApproveSelectedRecords}
                  size="small"
                />

                {/* 6. Modifiche pendenti */}
                <Q8Button
                  variant="secondary"
                  label={`Modifiche pendenti (${pendingChangesCount})`}
                  onClick={handlePendingChangesClick}
                  size="small"
                />

                {/* 7. Storico modifiche */}
                <Q8Button
                  variant="secondary"
                  label="Storico Modifiche"
                  icon={<HistoryIcon />}
                  onClick={handleHistoryClick}
                  size="small"
                />
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            my: 0.5,
            p: 0.5,
            bgcolor: "#FFF8E1",
            border: "1px solid #FFE082",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <InfoOutlined sx={{ color: "#FB8C00" }} />
          <Typography variant="body2">
            Per modificare un record, scegliere una colonna modificabile (colonne
            in giallo) ed effettuare un doppio click sulla cella per effettuare
            una modifica.
          </Typography>
        </Box>

        {(bestRole === "Admin" || bestRole === "Validator") && (
          <Box
            sx={{
              my: 0.5,
              p: 0.5,
              bgcolor: "#E8EAF6",
              border: "1px solid #9FA8DA",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <InfoOutlined sx={{ color: "#3F51B5" }} />
            <Typography variant="body2">
              <strong>Modalità {bestRole}:</strong> In qualità di{" "}
              {bestRole.toLowerCase()}, puoi modificare anche i record già
              approvati. I record approvati modificabili sono evidenziati con un
              bordo tratteggiato verde.
            </Typography>
          </Box>
        )}

        {/* <Box
          sx={{
            my: 1,
            p: 2,
            bgcolor: "#E8EAF6",
            border: "1px solid #9FA8DA",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InfoOutlined sx={{ color: "#3F51B5" }} />
          <Typography variant="body2">
            <strong>Filtri per data:</strong> dopo aver selezionato una data per
            filtrare, cliccare su un qualsiasi punto esterno al calendario per
            aggiornare la tabella.
          </Typography>
        </Box> */}

        {searchBoxVisible && (
          <Box
            sx={{
              width: { xs: "100%", sm: "500px", md: "1000px" },
              maxWidth: "100%",
              mb: 2,
            }}
          >
            <OpexSearchBox onSearch={handleSearch} userEmail={user?.email} />
          </Box>
        )}
      </div>
      <div className="table_container">
        <div ref={tableRef} className="custom_table"></div>
      </div>

      <ChangeHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        history={changeHistory || []}
        filters={historyFilters}
        onFiltersChange={setHistoryFilters}
      />

      <ColumnSelectorDialog
        open={columnSelectorOpen}
        onClose={() => setColumnSelectorOpen(false)}
        columns={columns}
        visibleColumns={visibleColumns}
        onVisibilityChange={handleColumnVisibilityChange}
      />

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma Approvazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Stai per approvare {selectedRecordsCount} record. Questa azione non
            può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={() => setConfirmDialogOpen(false)}
          />
          <Q8Button
            variant="primary"
            label="Conferma"
            onClick={confirmApproval}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default OpexTable;
